import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';

import { IconChevronRight } from '@tabler/icons-react';
import axios from 'axios';
import {
  chipTotal,
  DEFAULT_RANK_POINTS,
  exportSettlementLevelJson,
  LEVEL_JSON_MAX_BYTES,
  nonnegativeInteger,
  parseSettlementLevelJson,
  validLevel,
  type SettlementLevelInput,
} from './timed-settlement-level-json';

export interface SettlementLevel extends SettlementLevelInput {
  id: string;
}
type LevelInput = SettlementLevelInput;
export function editableLevel(level: SettlementLevel): LevelInput {
  return {
    name: level.name,
    startingChips: level.startingChips,
    initialCounts: { ...level.initialCounts },
    raCounts: { ...level.raCounts },
    activityFee1: level.activityFee1,
    prizeContribution: level.prizeContribution,
    participationPoints: level.participationPoints,
    rankPoints: [...(level.rankPoints ?? DEFAULT_RANK_POINTS)],
  };
}
const BUTTON =
  'relative min-h-11 rounded border border-base-300 px-3 py-2 text-sm text-base-content hover:bg-base-300 disabled:opacity-50';
const PRIMARY = `${BUTTON} border-primary bg-primary text-primary-content hover:bg-primary/90`;
const FIELD = 'mt-1 w-full rounded border border-base-300 bg-base-100 p-2 text-base-content';
const faces = [100, 500, 2500, 10000, 50000];
const blank = (): LevelInput => ({
  name: '',
  startingChips: 0,
  initialCounts: Object.fromEntries(faces.map((face) => [face, 0])),
  raCounts: Object.fromEntries(faces.map((face) => [face, 0])),
  activityFee1: 0,
  prizeContribution: 0,
  participationPoints: 0,
  rankPoints: [3, 2, 1],
});

export const parseRankPoints = (text: string): number[] | null => {
  const parts = text
    .split(/[,，\s]+/)
    .filter(Boolean)
    .map(Number);
  return parts.every((n) => Number.isSafeInteger(n) && n >= 0) && parts.length <= 50 ? parts : null;
};

export function useSettlementLevels() {
  return useQuery({
    queryKey: ['timed-settlement-levels'],
    queryFn: () => api.get<SettlementLevel[]>('/tournament/timed-settlement-levels'),
  });
}

export default function TimedSettlementLevelsPanel({
  showHeading = true,
}: {
  showHeading?: boolean;
}) {
  const { t } = useTranslation('tournament');
  const allowedActions = useAuthStore((state) => state.allowedActions);
  const canCreate = allowedActions?.includes('template:create') ?? false;
  const canUpdate = allowedActions?.includes('template:update') ?? false;
  const canDelete = allowedActions?.includes('template:delete') ?? false;
  const qc = useQueryClient();
  const { data: levels = [], isLoading, isError, refetch } = useSettlementLevels();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const operation = useRef(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<LevelInput>(blank);
  const [error, setError] = useState('');
  const [rankText, setRankText] = useState('3, 2, 1');
  const rankPoints = parseRankPoints(rankText);
  const denominations = [
    ...new Set([...Object.keys(form.initialCounts), ...Object.keys(form.raCounts)].map(Number)),
  ].sort((a, b) => a - b);
  const initialTotal = denominations.reduce(
    (sum, face) => sum + face * (form.initialCounts[String(face)] ?? 0),
    0,
  );
  const raTotal = denominations.reduce(
    (sum, face) => sum + face * (form.raCounts[String(face)] ?? 0),
    0,
  );
  const save = useMutation({
    mutationFn: (body: LevelInput) => {
      return editingId !== 'new'
        ? api.patch(`/tournament/timed-settlement-levels/${editingId}`, body)
        : api.post('/tournament/timed-settlement-levels', body);
    },
    onSuccess: () => {
      setEditingId(null);
      setForm(blank());
      setRankText('3, 2, 1');
      setError('');
      void qc.invalidateQueries({ queryKey: ['timed-settlement-levels'] });
    },
    onError: (failure: Error) => setError(errorMessage(failure)),
    onSettled: () => {
      operation.current = false;
    },
  });
  const edit = (level: SettlementLevel) => {
    setEditingId(level.id);
    setForm(editableLevel(level));
    setRankText((level.rankPoints ?? DEFAULT_RANK_POINTS).join(', '));
    setError('');
  };
  const changeCount = (kind: 'initialCounts' | 'raCounts', face: number, value: number) => {
    setForm((current) => ({
      ...current,
      [kind]: { ...current[kind], [face]: value },
    }));
  };
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/tournament/timed-settlement-levels/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['timed-settlement-levels'] });
    },
    onError: (failure: Error) => setError(errorMessage(failure)),
    onSettled: () => {
      operation.current = false;
    },
  });
  function errorMessage(failure: unknown) {
    const status = axios.isAxiosError(failure) ? failure.response?.status : undefined;
    return t(
      status === 409
        ? 'timedLevels.nameConflict'
        : status === 403
          ? 'timedLevels.forbidden'
          : status === 404
            ? 'timedLevels.unavailable'
            : status === 400
              ? 'timedLevels.invalidValues'
              : 'timedLevels.operationFailed',
    );
  }
  const busy = save.isPending || remove.isPending || importing;
  const showEditor = editingId !== null && (editingId === 'new' ? canCreate : canUpdate);
  const body = { ...form, name: form.name.trim(), startingChips: initialTotal, rankPoints };
  const valid = validLevel(body);
  const startCreate = (values = blank()) => {
    setEditingId('new');
    setForm(values);
    setRankText(values.rankPoints.join(', '));
    setError('');
  };
  const download = (level: SettlementLevel) => {
    setError('');
    try {
      const blob = new Blob([exportSettlementLevelJson(editableLevel(level))], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `timed-level-${level.name.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 80)}.json`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch {
      setError(t('timedLevels.operationFailed'));
    }
  };
  return (
    <section className="mb-6 space-y-4" aria-label={t('timedLevels.title')}>
      {showHeading && (
        <div>
          <h1 className="font-semibold">{t('timedLevels.title')}</h1>
          <p className="text-sm text-base-content/70">{t('timedLevels.description')}</p>
        </div>
      )}
      {!showEditor && canCreate && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={BUTTON}
            disabled={busy}
            onClick={() => fileInput.current?.click()}
          >
            {t('importJson')}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept=".json,application/json"
            className="hidden"
            aria-label={t('importJson')}
            onChange={async (event) => {
              const input = event.currentTarget;
              const file = input.files?.[0];
              if (!file || operation.current) return;
              operation.current = true;
              setImporting(true);
              setError('');
              try {
                if (file.size > LEVEL_JSON_MAX_BYTES) throw Error('size');
                const values = parseSettlementLevelJson(await file.text());
                startCreate(values);
              } catch {
                setError(t('timedLevels.invalidJson'));
              } finally {
                input.value = '';
                operation.current = false;
                setImporting(false);
              }
            }}
          />
          <button
            type="button"
            className={PRIMARY}
            disabled={busy || isLoading}
            onClick={() => startCreate()}
          >
            {t('timedLevels.add')}
          </button>
        </div>
      )}
      {importing && <p role="status">{t('timedLevels.importing')}</p>}
      {error && (
        <p role="alert" className="border border-error p-3 text-error text-sm">
          {error}
        </p>
      )}
      {showEditor && (
        <form
          className="rounded border border-base-300 bg-base-200 p-4 sm:p-6 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!validLevel(body) || busy || operation.current) return;
            operation.current = true;
            setError('');
            save.mutate(body);
          }}
        >
          <h2 className="text-lg font-bold">
            {editingId === 'new' ? t('timedLevels.add') : t('timedLevels.edit')}
          </h2>
          {editingId === 'new' && (
            <p className="text-sm text-base-content/70">{t('timedLevels.draftHint')}</p>
          )}
          <fieldset disabled={busy} className="space-y-4 min-w-0">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {(
                [
                  ['name', t('timedLevels.name')],
                  ['activityFee1', t('timedLevels.activityFee1')],
                  ['prizeContribution', t('timedLevels.prizeContribution')],
                  ['participationPoints', t('timedLevels.participationPoints')],
                ] as const
              ).map(([key, label]) => (
                <label key={key} className="text-sm">
                  {label}
                  <input
                    className={FIELD}
                    autoFocus={key === 'name'}
                    type={key === 'name' ? 'text' : 'number'}
                    maxLength={key === 'name' ? 128 : undefined}
                    required
                    step={key === 'name' ? undefined : 1}
                    aria-invalid={
                      key === 'name' ? !form.name.trim() : !nonnegativeInteger(form[key])
                    }
                    min={key === 'name' ? undefined : 0}
                    value={form[key]}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        [key]: key === 'name' ? event.target.value : Number(event.target.value),
                      })
                    }
                  />
                </label>
              ))}
              <label className="text-sm">
                {t('timedLevels.rankPoints')}
                <input
                  className={FIELD}
                  type="text"
                  inputMode="numeric"
                  value={rankText}
                  aria-invalid={rankPoints === null}
                  onChange={(event) => setRankText(event.target.value)}
                />
                <span className="text-xs text-base-content/60">
                  {t('timedLevels.rankPointsHint')}
                </span>
              </label>
            </div>
            <p className="my-2 text-sm text-base-content/70">
              {t('timedLevels.netContribution', {
                gross: form.prizeContribution,
                fee: form.activityFee1,
                net: form.prizeContribution - form.activityFee1,
              })}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <caption className="sr-only">{t('timedLevels.chipTable')}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t('timedLevels.denomination')}</th>
                    <th scope="col">{t('timedLevels.initialCounts')}</th>
                    <th scope="col">{t('timedLevels.raCounts')}</th>
                  </tr>
                </thead>
                <tbody>
                  {denominations.map((face) => (
                    <tr key={face}>
                      <th scope="row" className="pr-2 text-right tabular-nums">
                        {face.toLocaleString()}
                      </th>
                      {(['initialCounts', 'raCounts'] as const).map((kind) => (
                        <td key={kind}>
                          <input
                            className={`${FIELD} min-w-24 text-right tabular-nums`}
                            aria-label={t('timedLevels.countField', {
                              denomination: face.toLocaleString(),
                              kind: t(`timedLevels.${kind}`),
                            })}
                            aria-invalid={!nonnegativeInteger(form[kind][String(face)] ?? 0)}
                            required
                            type="number"
                            min="0"
                            step="1"
                            value={form[kind][String(face)] ?? 0}
                            onChange={(event) =>
                              changeCount(kind, face, Number(event.target.value))
                            }
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!denominations.includes(100000) && (
              <button
                type="button"
                className={BUTTON}
                onClick={() =>
                  setForm((current) => ({
                    ...current,
                    initialCounts: { ...current.initialCounts, '100000': 0 },
                    raCounts: { ...current.raCounts, '100000': 0 },
                  }))
                }
              >
                {t('timedLevels.addDenomination')}
              </button>
            )}
            <p className="text-sm">
              {t('timedLevels.totals', {
                initial: initialTotal.toLocaleString(),
                ra: raTotal.toLocaleString(),
              })}
            </p>
          </fieldset>
          <div className="flex flex-wrap gap-2">
            <button type="submit" className={PRIMARY} disabled={busy || !valid}>
              {save.isPending
                ? t('loading', { ns: 'common' })
                : editingId === 'new'
                  ? t('timedLevels.save')
                  : t('timedLevels.update')}
            </button>
            <button
              type="button"
              className={BUTTON}
              disabled={busy}
              onClick={() => {
                setEditingId(null);
                setForm(blank());
                setError('');
              }}
            >
              {t('cancel', { ns: 'common' })}
            </button>
          </div>
        </form>
      )}
      {isLoading ? (
        <p role="status" className="py-8 text-base-content/70">
          {t('loading', { ns: 'common' })}
        </p>
      ) : isError ? (
        <div role="alert" className="py-8 space-y-3">
          <p>{t('timedLevels.loadError')}</p>
          <button type="button" className={BUTTON} onClick={() => void refetch()}>
            {t('retry')}
          </button>
        </div>
      ) : levels.length === 0 ? (
        <p className="py-8 text-center text-base-content/70">{t('timedLevels.empty')}</p>
      ) : (
        <ul className="space-y-3">
          {levels.map((level) => {
            const expanded = expandedIds.has(level.id);
            const detailsId = `timed-level-${level.id}`;
            return (
              <li key={level.id} className="rounded border border-base-300 bg-base-200">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <h2 className="text-lg font-bold">
                      <button
                        type="button"
                        className="flex min-h-11 w-full items-center gap-2 text-left hover:text-primary"
                        aria-expanded={expanded}
                        aria-controls={detailsId}
                        onClick={() =>
                          setExpandedIds((current) => {
                            const next = new Set(current);
                            if (next.has(level.id)) next.delete(level.id);
                            else next.add(level.id);
                            return next;
                          })
                        }
                      >
                        <IconChevronRight
                          size={18}
                          aria-hidden="true"
                          className={`shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`}
                        />
                        <span className="min-w-0 [overflow-wrap:anywhere]">{level.name}</span>
                      </button>
                    </h2>
                    <p className="pl-7 text-sm text-base-content/70">
                      {t('timedLevels.totals', {
                        initial: level.startingChips.toLocaleString(),
                        ra: chipTotal(level.raCounts).toLocaleString(),
                      })}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    {canUpdate && (
                      <button
                        type="button"
                        className={BUTTON}
                        disabled={busy || editingId !== null}
                        onClick={() => edit(level)}
                      >
                        {t('edit', { ns: 'common' })}
                        <span className="sr-only"> {level.name}</span>
                      </button>
                    )}
                    <button
                      type="button"
                      className={BUTTON}
                      disabled={busy}
                      onClick={() => download(level)}
                    >
                      {t('exportJson')}
                      <span className="sr-only"> {level.name}</span>
                    </button>
                    {canDelete && (
                      <button
                        type="button"
                        className={`${BUTTON} border-error text-error`}
                        disabled={busy || editingId !== null}
                        onClick={() => {
                          if (
                            operation.current ||
                            !confirm(t('timedLevels.confirmDelete', { name: level.name }))
                          )
                            return;
                          operation.current = true;
                          setError('');
                          remove.mutate(level.id);
                        }}
                      >
                        {remove.isPending && remove.variables === level.id
                          ? t('loading', { ns: 'common' })
                          : t('delete', { ns: 'common' })}
                        <span className="sr-only"> {level.name}</span>
                      </button>
                    )}
                  </div>
                </div>
                {expanded && (
                  <div id={detailsId} className="px-4 pb-4 space-y-4">
                    <dl className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      {(
                        [
                          ['activityFee1', level.activityFee1.toLocaleString()],
                          ['prizeContribution', level.prizeContribution.toLocaleString()],
                          ['participationPoints', level.participationPoints.toLocaleString()],
                          [
                            'rankPoints',
                            (level.rankPoints ?? DEFAULT_RANK_POINTS).join(', ') || '—',
                          ],
                        ] as const
                      ).map(([key, value]) => (
                        <div key={key}>
                          <dt className="text-sm text-base-content/70">
                            {t(`timedLevels.${key}`)}
                          </dt>
                          <dd className="font-semibold tabular-nums">{value}</dd>
                        </div>
                      ))}
                    </dl>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <caption className="sr-only">
                          {t('timedLevels.chipTable')} — {level.name}
                        </caption>
                        <thead>
                          <tr>
                            <th scope="col" className="px-2 py-2 text-right">
                              {t('timedLevels.denomination')}
                            </th>
                            <th scope="col" className="px-2 py-2 text-right">
                              {t('timedLevels.initialCounts')}
                            </th>
                            <th scope="col" className="px-2 py-2 text-right">
                              {t('timedLevels.raCounts')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            ...new Set([
                              ...Object.keys(level.initialCounts),
                              ...Object.keys(level.raCounts),
                            ]),
                          ]
                            .sort((a, b) => Number(a) - Number(b))
                            .map((face) => (
                              <tr key={face} className="border-t border-base-300">
                                <th scope="row" className="px-2 py-2 text-right tabular-nums">
                                  {Number(face).toLocaleString()}
                                </th>
                                <td className="px-2 py-2 text-right tabular-nums">
                                  {(level.initialCounts[face] ?? 0).toLocaleString()}
                                </td>
                                <td className="px-2 py-2 text-right tabular-nums">
                                  {(level.raCounts[face] ?? 0).toLocaleString()}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
